<script setup>
import { t, chapterTitle } from '../lib/i18n.mjs'
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import AppIcon from './AppIcon.vue'
import { narrativeTrack, narrativeIndex } from '../lib/narrative.mjs'
const props = defineProps({ document: Object, selection: Object, busy: Boolean })
const emit = defineEmits(['select'])
const track = computed(() => narrativeTrack(props.document))
const position = ref(0),
  playing = ref(false),
  rail = ref(null),
  railWidth = ref(240)
const currentIndex = computed(() => narrativeIndex(track.value.segments, position.value))
const current = computed(() => track.value.segments[currentIndex.value])
const percent = (value) => (track.value.duration ? (value / track.value.duration) * 100 : 0)
const chapterMarks = computed(() => {
  const marks = track.value.segments.filter((s, i, all) => !i || s.chapterId !== all[i - 1].chapterId)
  let previous = -Infinity
  return marks.map((segment) => {
    const x = (percent(segment.start) / 100) * railWidth.value
    const labelX = Math.max(7, x, previous + 19)
    const showLabel = labelX <= railWidth.value - 7
    if (showLabel) previous = labelX
    return { ...segment, labelOffset: labelX - x, showLabel }
  })
})
const labeledTicks = computed(() => {
  const result = new Set(),
    positions = []
  const segments = track.value.segments
  for (const index of [currentIndex.value, 0, segments.length - 1, ...segments.map((s) => s.index)]) {
    if (index < 0 || index >= segments.length || result.has(index)) continue
    const x = (percent(segments[index].start) / 100) * railWidth.value
    if (positions.every((other) => Math.abs(x - other) >= 31)) {
      result.add(index)
      positions.push(x)
    }
  }
  return result
})
let frame,
  previousTime = null,
  lastEmitted = null,
  dragging = false,
  observer
function pause() {
  playing.value = false
  previousTime = null
  cancelAnimationFrame(frame)
}
function publish() {
  if (current.value && current.value.id !== lastEmitted) {
    lastEmitted = current.value.id
    emit('select', { kind: 'paragraph', id: current.value.id, origin: 'narrative' })
  }
}
function tick(now) {
  if (!playing.value) return
  if (previousTime !== null)
    position.value = Math.min(track.value.duration, position.value + now - previousTime)
  previousTime = now
  publish()
  if (position.value >= track.value.duration) pause()
  else frame = requestAnimationFrame(tick)
}
function play() {
  if (props.busy || !current.value) return
  if (position.value >= track.value.duration) position.value = 0
  lastEmitted = null
  publish()
  playing.value = true
  previousTime = null
  frame = requestAnimationFrame(tick)
}
function jump(index) {
  if (props.busy || !track.value.segments.length) return
  const resume = playing.value
  pause()
  position.value = track.value.segments[Math.max(0, Math.min(track.value.segments.length - 1, index))].start
  lastEmitted = null
  publish()
  if (resume) play()
}
function seek(event) {
  const box = rail.value.getBoundingClientRect()
  position.value = Math.max(0, Math.min(1, (event.clientX - box.left) / box.width)) * track.value.duration
  publish()
}
function startScrub(event) {
  if (event.button !== 0 || props.busy || !current.value) return
  event.preventDefault()
  pause()
  lastEmitted = null
  dragging = true
  rail.value.focus()
  rail.value.setPointerCapture(event.pointerId)
  seek(event)
}
function endScrub(event) {
  dragging = false
  if (rail.value?.hasPointerCapture(event.pointerId)) rail.value.releasePointerCapture(event.pointerId)
}
function key(event) {
  const step = { ArrowLeft: -1, ArrowRight: 1, ArrowDown: -1, ArrowUp: 1, PageUp: -5, PageDown: 5 }[event.key]
  if (step !== undefined || ['Home', 'End', ' ', 'Enter'].includes(event.key)) {
    event.preventDefault()
    if (step !== undefined) jump(currentIndex.value + step)
    else if (event.key === 'Home') jump(0)
    else if (event.key === 'End') jump(track.value.segments.length - 1)
    else if (playing.value) pause()
    else play()
  }
}
function visibility() {
  if (window.document.hidden) pause()
}
watch(
  () => props.document.id,
  () => {
    pause()
    position.value = 0
    lastEmitted = null
  },
)
watch(
  () => props.busy,
  (value) => {
    if (value) pause()
  },
)
watch(
  () => props.selection,
  (selection) => {
    if (selection?.origin === 'narrative' && selection.id === lastEmitted) return
    pause()
    lastEmitted = null
    const id =
      selection?.kind === 'unit'
        ? props.document.units.find((u) => u.id === selection.id)?.paragraphId
        : selection?.kind === 'paragraph'
          ? selection.id
          : null
    const segment = track.value.segments.find((s) => s.id === id)
    if (segment) position.value = segment.start
  },
)
onMounted(() => {
  observer = new ResizeObserver((entries) => {
    railWidth.value = entries[0].contentRect.width
  })
  observer.observe(rail.value)
  window.document.addEventListener('visibilitychange', visibility)
})
onUnmounted(() => {
  pause()
  observer?.disconnect()
  window.document.removeEventListener('visibilitychange', visibility)
})
</script>
<template>
  <div class="narrative-player" :aria-label="t('文章叙述顺序')">
    <div class="narrative-controls">
      <button
        class="narrative-play"
        :disabled="busy || !current"
        :aria-label="playing ? t('暂停叙述') : t('播放叙述顺序')"
        :title="playing ? t('暂停') : t('按文章顺序播放')"
        @click="playing ? pause() : play()"
      >
        <AppIcon :name="playing ? 'Pause' : 'Play'" :size="13" />
      </button>
      <button
        :disabled="busy || currentIndex <= 0"
        :aria-label="t('上一个段落')"
        :title="t('上一个段落')"
        @click="jump(currentIndex - 1)"
      >
        <AppIcon name="ChevronLeft" :size="14" />
      </button>
      <button
        :disabled="busy || currentIndex < 0 || currentIndex >= track.segments.length - 1"
        :aria-label="t('下一个段落')"
        :title="t('下一个段落')"
        @click="jump(currentIndex + 1)"
      >
        <AppIcon name="ChevronRight" :size="14" />
      </button>
    </div>
    <div
      ref="rail"
      class="narrative-rail"
      :class="{ 'is-playing': playing, 'is-disabled': busy || !current }"
      role="slider"
      tabindex="0"
      :aria-label="t('文章叙述进度')"
      aria-orientation="horizontal"
      :aria-valuemin="1"
      :aria-valuemax="Math.max(1, track.segments.length)"
      :aria-valuenow="currentIndex + 1 || 1"
      :aria-valuetext="
        current
          ? t('{0}，第 {1} / {2} 段', { 0: current.id, 1: currentIndex + 1, 2: track.segments.length })
          : t('暂无段落')
      "
      :aria-disabled="busy || !current"
      @pointerdown="startScrub"
      @pointermove="dragging && seek($event)"
      @pointerup="endScrub"
      @pointercancel="endScrub"
      @lostpointercapture="dragging = false"
      @keydown="key"
    >
      <div class="narrative-track" />
      <div class="narrative-progress" :style="{ width: `${percent(position)}%` }" />
      <span
        v-for="segment in track.segments"
        :key="segment.id"
        class="narrative-tick"
        :class="{ 'is-current': segment.index === currentIndex, 'is-past': segment.start < position }"
        :style="{ left: `${percent(segment.start)}%` }"
        :title="`${segment.id} · ${segment.title}`"
        ><i /><span v-if="labeledTicks.has(segment.index)" :class="{ 'first-label': segment.index === 0 }">{{
          segment.label
        }}</span></span
      >
      <span
        v-for="segment in chapterMarks"
        :key="`chapter-${segment.id}`"
        class="narrative-chapter"
        :style="{ left: `${percent(segment.start)}%` }"
        :title="chapterTitle(segment.chapter, document) || t('引言')"
        ><span v-if="segment.showLabel" :style="{ marginLeft: `${segment.labelOffset}px` }">{{
          segment.chapter ? String(segment.chapter.number).padStart(2, '0') : t('序')
        }}</span
        ><i
      /></span>
      <span class="narrative-playhead" :style="{ left: `${percent(position)}%` }" />
    </div>
    <output class="narrative-position" :title="current ? `${current.id} · ${current.title}` : ''"
      >{{ current?.label || '—' }}<small>{{ currentIndex + 1 }} / {{ track.segments.length }}</small></output
    >
  </div>
</template>
